import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AssignableWellspringRole,
  ListWellspringUsersResponse,
  SetWellspringRoleRequest,
  WellspringUser,
} from '@metron/shared';
import { useApiClient } from './useApiClient.ts';

export const WELLSPRING_USERS_QUERY_KEY = ['wellspring-users'] as const;

/** Users in Wellspring's own pool with their assigned Wellspring role. SuperAdmin only. */
export function useWellspringUsers() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: WELLSPRING_USERS_QUERY_KEY,
    queryFn: async () => {
      const data = await apiClient.get<ListWellspringUsersResponse>('wellspring-users');
      return data.users;
    },
  });

  const setRole = useMutation({
    mutationFn: (
      { username, role }: { username: string; role: AssignableWellspringRole | null },
    ) => {
      const body: SetWellspringRoleRequest = { role };
      return apiClient.put<WellspringUser>(
        `wellspring-users/${encodeURIComponent(username)}/role`,
        body,
      );
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: WELLSPRING_USERS_QUERY_KEY }),
  });

  return { users: query.data ?? [], isLoading: query.isLoading, error: query.error, setRole };
}
